import type { FAQ, LandingPageFeature } from "@brookbush/types";

import type { PortableTextBlock } from "@portabletext/types";

import { BRAND_ICON_THUMBNAIL_URL } from "./landingPageFeatureThumbnail";

export const CPT_PROGRAMS_PAGE_SLUG = "certified-personal-trainer" as const;
export const BI_CPT_CERTIFICATION_SLUG = "certified-personal-trainer" as const;

const plainBlocks = (text: string): PortableTextBlock[] => [
	{
		_type: "block",
		_key: "a",
		style: "normal",
		markDefs: [],
		children: [{ _type: "span", _key: "s", text, marks: [] }],
	},
];

export const isCptProgramsPageSlug = (
	slug: string | undefined | null,
): boolean => slug === CPT_PROGRAMS_PAGE_SLUG;

/**
 * Code-owned copy for /info/certified-personal-trainer (Sep 22, 2026 build doc).
 * NCCA mark homes are the only places the mark may appear on this page.
 */
export const CPT_PROGRAMS_PAGE = {
	seoTitle:
		"Personal Trainer Certification | NCCA-Accredited Exam + BI-CPT Certificate",
	seoDescription:
		"Get certified: the NCCA-accredited Brookbush Institute CPT exam, plus the Advanced BI-CPT Certificate and the Study Plan, Practice Exam, and Exam Coach — all in a monthly membership.",
	h1: "Certified Personal Trainer (CPT) Certification, Advanced Certificate, and Study Programs",
	heroLine: "Get educated. Get certified. Get hired.",
	heroText:
		"The only comprehensively evidence-based education platform in fitness now offers an NCCA-accredited CPT Certification exam.",
	studyPlanHref: "/certifications/cpt-certification-study-plan",
	biCptHref: `/certifications/${BI_CPT_CERTIFICATION_SLUG}`,
	examPortalHref: "/exam-portal",
	membershipPriceLabel: "Become a member — $29.99/month →",
	examPortalCtaLabel: "Go to the exam portal →",
	trustLine:
		"100s of 5-star Google reviews · Self-paced, desktop and mobile · Cancel anytime",
	footerCtaSentence:
		"The BI is the only approved continuing-education provider with a true monthly membership model (cancel anytime) — and the home of the NCCA-accredited Brookbush Institute CPT Certification exam.",
	nccaMarkHomes: [
		"hero-exam-row",
		"two-ways-exam",
		"accreditations-ncca",
	] as const,
	/** Opaque B&W shield for white/light tiles (transparent ink logo is invisible there). */
	biShieldSrc: "/logos/brookbush-institute-logo-icon-on-light.png",
	nccaAccreditation: {
		title: "National Commission for Certifying Agencies (NCCA)",
		body: "On September 22, 2026, the National Commission for Certifying Agencies (NCCA) granted accreditation to the Brookbush Institute Certified Personal Trainer (CPT) Certification Program for demonstrating compliance with the NCCA Standards for the Accreditation of Certification Programs. NCCA is the accrediting body of the Institute for Credentialing Excellence. The accreditation applies to the CPT Certification examination.",
		certificateAlt:
			"NCCA Certificate of Accreditation — Brookbush Institute Certified Personal Trainer (CPT) Certification Program",
	},
} as const;

export const getCptProgramsFaqs = (cmsFaqs: FAQ[] = []): FAQ[] => {
	const entries: { question: string; answer: string }[] = [
		{
			question: "What is a Certified Personal Trainer (CPT)?",
			answer:
				"A Certified Personal Trainer is a fitness professional who evaluates a client's fitness level, sets achievable goals, and designs and coaches personalized exercise programs — selecting the right exercises, tailoring them to the client's abilities, and guiding performance so the client reaches their goals safely.",
		},
		{
			question: "Why get certified?",
			answer:
				"Because most gyms require certification for employment, liability insurers require it for coverage, and clients expect it. Certified trainers work from science-backed principles, which is what separates results from guesswork.",
		},
		{
			question: "Do I need an NCCA-accredited certification?",
			answer:
				"Many U.S. employers require one as a hiring condition. If yours does, the Brookbush Institute CPT Certification exam is NCCA-accredited and open to anyone. Some employers accept the BI-CPT Certificate for employment on the strength of the education — Equinox, Life Time, and GoodLife Fitness among them.",
		},
		{
			question: "Can I get certified online?",
			answer:
				"Yes. The BI-CPT is earned entirely online, at your pace. The CPT Certification exam is delivered online with live remote proctoring, or at an authorized testing center if you prefer.",
		},
		{
			question: "How long does the BI-CPT take?",
			answer:
				"You earn credits with each course, and fifty credits earns the certificate. There's no single final exam; you take short exams as you go. Members report 30 to 70 hours of study, and have finished in as little as a week or over the course of a year — the pacing is yours.",
		},
		{
			question: "How much does it cost?",
			answer:
				"Membership is $29.99 a month, cancel anytime, and includes the BI-CPT, the Study Plan, the Practice Exam, the Exam Coach, and every course on the platform. The CPT Certification exam fee is separate: $399, with retakes at $199. For a full comparison of certification costs in the industry, including the fees that don't appear on the price tag, read How Much Does a Personal Training Certification Cost (Including Hidden Fees)? at https://brookbushinstitute.com/articles/how-much-does-a-personal-training-certification-cost-including-hidden-fees",
		},
		{
			question:
				"What is the difference between the BI-CPT Certificate and the CPT Certification?",
			answer:
				"The BI-CPT Certificate is an advanced, education-based credential awarded by the Brookbush Institute. It reflects mastery of an evidence-based, systematic, client-centered, and outcome-driven approach to personal training, earned through a credit-based, iterative testing process on the Institute's online platform, and included with an active membership. The CPT Certification exam is a standalone assessment, accredited by the National Commission for Certifying Agencies (NCCA) on September 22, 2026, that verifies a candidate meets the competency standard for certification. It is separate from Institute educational programs and credentials: completing Institute courses provides no points, credit, or scoring advantage, and no course or membership is required to sit for it. The CPT credential is earned solely by passing the proctored exam, online or in person, and the exam fee is separate from membership. Details: https://brookbushinstitute.com/exam-portal",
		},
		{
			question: "Do gyms accept this CPT certificate?",
			answer:
				"Some do, on the strength of the education: Equinox, Life Time, and GoodLife Fitness accept the BI-CPT Certificate for employment. Many U.S. employers require an NCCA-accredited certification as a hiring condition. If yours does, the Brookbush Institute CPT Certification exam is NCCA-accredited and open to anyone — and the BI-CPT curriculum, the Study Plan, the Practice Exam, and the Exam Coach are how we recommend you prepare for it.",
		},
	];

	const buildDocFaqs = entries.map((entry, i) => ({
		_key: `cpt-faq-${i}`,
		question: entry.question,
		answer: plainBlocks(entry.answer),
	}));

	const buildDocQuestions = new Set(
		buildDocFaqs.map((faq) => faq.question.toLowerCase()),
	);

	const preservedCmsFaqs = cmsFaqs.filter(
		(faq) => !buildDocQuestions.has(faq.question.toLowerCase()),
	);

	return [...buildDocFaqs, ...preservedCmsFaqs];
};

const SELF_PACED_DESCRIPTION =
	"Study at your own pace. The BI-CPT has no single final exam — you earn credit as you go, with short practice and final tests at the end of each course.";

const PEER_REVIEWED_DESCRIPTION =
	"Every Brookbush Institute course is built on a systematic review of the peer-reviewed research, and our curriculum has been reviewed by independent subject-matter experts.";

const STUDY_COMPANION_DESCRIPTION =
	"Members get the Study Plan, the Practice Exam (100 randomized questions, never stale), and the Exam Coach, which tells you exactly which courses to study next — everything we built to prepare you for the NCCA-accredited CPT Certification exam.";

/**
 * Overrides feature tiles for the CPT programs page per the build doc.
 * Drops ACofE (or retitles to Peer-Reviewed Curriculum) and adds Study Companion.
 */
export const getCptProgramsFeatures = (
	cmsFeatures: LandingPageFeature[],
): LandingPageFeature[] => {
	const mapped = cmsFeatures
		.map((feature) => {
			if (/Self-paced/i.test(feature.title)) {
				return { ...feature, description: SELF_PACED_DESCRIPTION };
			}
			if (/American Council of Education|ACofE/i.test(feature.title)) {
				return {
					...feature,
					title: "Peer-Reviewed Curriculum",
					description: PEER_REVIEWED_DESCRIPTION,
				};
			}
			return feature;
		})
		.filter(
			(feature) => !/American Council of Education|ACofE/i.test(feature.title),
		);

	const hasPeerReviewed = mapped.some(
		(f) => f.title === "Peer-Reviewed Curriculum",
	);
	const hasStudyCompanion = mapped.some(
		(f) => f.title === "The Perfect Study Companion",
	);

	const result = [...mapped];

	if (!hasPeerReviewed) {
		const acofe = cmsFeatures.find((f) =>
			/American Council of Education|ACofE/i.test(f.title),
		);
		result.push({
			_key: acofe?._key ?? "peer-reviewed-curriculum",
			title: "Peer-Reviewed Curriculum",
			description: PEER_REVIEWED_DESCRIPTION,
			thumbnail: acofe?.thumbnail ?? {
				url: BRAND_ICON_THUMBNAIL_URL,
				alt: "Peer-Reviewed Curriculum",
			},
		});
	}

	if (!hasStudyCompanion) {
		result.push({
			_key: "perfect-study-companion",
			title: "The Perfect Study Companion",
			description: STUDY_COMPANION_DESCRIPTION,
			thumbnail: {
				url: BRAND_ICON_THUMBNAIL_URL,
				alt: "The Perfect Study Companion",
			},
		});
	}

	return result;
};
